// Shared public-facing header and audit plan components
// FirmHeader fetches from Supabase so external devices get correct branding
import React, { useState, useEffect } from 'react'
import { oneLine } from '../lib/oneLine'
import { format, parseISO } from 'date-fns'
import { supabase } from '../lib/supabase'
import { useNarrow } from '../lib/useNarrow'

// ─── FirmHeader ───────────────────────────────────────────────────────────────
// Layout: [logo OR name] [details]
// Fetches from Supabase on mount so client/auditor devices get correct data
// accredited={false}: pages for services outside the SAC accreditation (client internal audit) — no SAC line.
export function FirmHeader({ accredited = true }) {
  const [s, setS] = useState({
    name:    localStorage.getItem('firm_name')     || 'CCIS Singapore Pte Ltd',
    address: localStorage.getItem('firm_address')  || '16 Jalan Kilang Timor #08-03, Redhill Forum Singapore 159308',
    phone:   localStorage.getItem('firm_phone')    || '(65) 6376 0550',
    email:   localStorage.getItem('firm_email')    || 'query@ccis.com.sg',
    logo:    localStorage.getItem('firm_logo_url') || '',
  })
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    // Public details only (name, address, phone, email, logo) — the stamp is never sent to public pages.
    supabase.rpc('get_public_firm_info').then(({ data }) => {
      if (!data) return
      const logo = data.firm_logo_base64 || ''
      if (data.firm_name)    localStorage.setItem('firm_name',     data.firm_name)
      if (data.firm_address) localStorage.setItem('firm_address',  data.firm_address)
      if (data.firm_phone)   localStorage.setItem('firm_phone',    data.firm_phone)
      if (data.firm_email)   localStorage.setItem('firm_email',    data.firm_email)
      if (logo)              localStorage.setItem('firm_logo_url', logo)
      setS(prev => ({
        name:    data.firm_name    || prev.name,
        address: data.firm_address || prev.address,
        phone:   data.firm_phone   || prev.phone,
        email:   data.firm_email   || prev.email,
        logo,
      }))
      setImgFailed(false)
    })
  }, [])

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      marginBottom: 20, paddingBottom: 16, borderBottom: '2px solid #006363',
    }}>
      {/* LEFT: logo if available, name text only as last-resort fallback */}
      {(s.logo && !imgFailed) ? (
        <img
          src={s.logo}
          alt={s.name}
          onError={() => setImgFailed(true)}
          style={{ height: 52, maxWidth: 200, objectFit: 'contain', objectPosition: 'left center', flexShrink: 0 }}
        />
      ) : (
        <div style={{ fontWeight: 700, fontSize: 15, color: '#006363', flexShrink: 0, maxWidth: 160 }}>
          {s.name}
        </div>
      )}

      {/* RIGHT: name (bold green) + SAC line + address + contact (all grey) */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#006363' }}>{s.name}</div>
        {accredited && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>SAC Accredited Auditing Organisation</div>}
        <div style={{ fontSize: 12, color: '#666' }}>{s.address}</div>
        <div style={{ fontSize: 12, color: '#666' }}>T: {s.phone} · E: {s.email}</div>
      </div>
    </div>
  )
}

// Per-day time span (first start–last end) read from the actual generated plan rows.
function computeDaySpans(plan) {
  const acc = {}; let cur = 1
  for (const r of plan || []) {
    if (r.isDayHeader) { const m = String(r.time).match(/(\d+)/); cur = m ? parseInt(m[1]) : cur + 1; continue }
    const rng = String(r.time).match(/^(\d{2})(\d{2})-(\d{2})(\d{2})$/)
    const one = String(r.time).match(/^(\d{2})(\d{2})$/)
    let s = null, e = null
    if (rng) { s = (+rng[1]) * 60 + (+rng[2]); e = (+rng[3]) * 60 + (+rng[4]) }
    else if (one) { s = e = (+one[1]) * 60 + (+one[2]) }
    if (s != null) { if (!acc[cur]) acc[cur] = { first: s, last: e }; else acc[cur].last = e }
  }
  return acc
}

// ─── Audit details table ──────────────────────────────────────────────────────
export function AuditDetailsSection({ job }) {
  const narrow = useNarrow()
  const firmName = localStorage.getItem('firm_name') || 'CCIS Singapore Pte Ltd'
  // Source of truth for the day span is the actual generated plan (day-header rows) + captured day dates.
  const plan = job.plan_rows_edited || job.plan_rows || []
  const twoDay = plan.some(r => r.isDayHeader)
  const spans = computeDaySpans(plan)
  const hm = m => String(Math.floor(m / 60)).padStart(2, '0') + String(m % 60).padStart(2, '0')
  const spanStr = n => spans[n] ? `${hm(spans[n].first)}–${hm(spans[n].last)}` : ''
  const fmtDay = d => { try { return d ? format(parseISO(d), 'd MMMM yyyy') : 'TBD' } catch (e) { return 'TBD' } }
  const dateRows = twoDay
    ? [
        ['Day 1', `${fmtDay(job.audit_date)}${spanStr(1) ? ' · ' + spanStr(1) : ''}`],
        ['Day 2', `${fmtDay(job.audit_date_day2)}${spanStr(2) ? ' · ' + spanStr(2) : ''}`],
      ]
    : [
        ['Audit date', job.audit_date ? format(parseISO(job.audit_date), 'd MMMM yyyy') : 'TBD'],
        ['Time of Audit', job.audit_time_display || spanStr(1) || '—'],
      ]
  const combinedCompanies = Array.isArray(job.companies) && job.companies.length > 1 ? job.companies : null
  const rows = [
    ['Auditee organisation', combinedCompanies
      ? <>{combinedCompanies.map((co, i) => <div key={i}>({i + 1}) {co.name || '—'}{co.uen ? ` (UEN ${co.uen})` : ''}</div>)}</>
      : (job.clients?.name || '—')],
    ['Audit type', job.audit_type || '—'],
    ...dateRows,
    ['HQ address', job.clients?.address || '—'],
    ['Audit site address', oneLine(job.audit_site_address) || '—'],
    ['Audit objectives', job.audit_objective || '—'],
    ['Audit criteria', job.audit_criteria || '—'],
    ['Audit scope', job.audit_scope_text || job.business_nature || '—'],
    ['Auditing organisation', firmName],
    ['RM Leader', job.rm_leader_name
      ? `${job.rm_leader_name}${job.rm_leader_phone ? ' · ' + job.rm_leader_phone : ''}` : '—'],
    // Plain text only — auditor name is NOT clickable and the auditor email is never exposed to the client.
    ['Assigned Auditor(s)', [job.auditor_name, job.auditor2_name].filter(Boolean).join(', ') || '—'],
    ['SAC Observer', job.observer_name || 'Nil'],
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{
          display: 'grid', gridTemplateColumns: narrow ? '1fr' : '200px 1fr', gap: narrow ? 1 : 8,
          fontSize: 13, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.05)',
        }}>
          <span style={{ color: '#888', fontWeight: 500 }}>{label}</span>
          <span style={{
            fontWeight: (['Audit date', 'Time of Audit', 'Day 1', 'Day 2', 'CT17 complexity'].includes(label)) ? 600 : 400,
            color: ['Audit date', 'Day 1', 'Day 2', 'Time of Audit'].includes(label) ? '#006363'
              : label === 'CT17 complexity' ? (
                  value.startsWith('High') ? '#791F1F'
                  : value.startsWith('Medium') ? '#854F0B'
                  : value.startsWith('Pending') ? '#4A1D96'
                  : '#27500a'
                )
              : 'inherit',
          }}>{value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Schedule row ─────────────────────────────────────────────────────────────
function ScheduleRow({ row, i, parallelMode }) {
  if (row.isDayHeader) return (
    <div style={{
      padding: '8px 0 4px', fontWeight: 700, fontSize: 12, color: '#006363',
      borderTop: i > 0 ? '2px solid #006363' : 'none', marginTop: i > 0 ? 8 : 0,
    }}>— {row.time} —</div>
  )
  if (row.time === 'PARALLEL_HEADER') return (
    <div style={{
      display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '0 8px',
      fontWeight: 600, fontSize: 11, paddingBottom: 4, color: '#006363',
      borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 4,
    }}>
      <div>Time</div><div>Auditor 1</div><div>Auditor 2</div>
    </div>
  )
  if (row.parallel) return (
    <div style={{
      display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '0 8px',
      fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)',
      background: 'rgba(0,99,99,0.04)', alignItems: 'start',
    }}>
      <div style={{ color: '#888', fontSize: 11 }}>{row.time}</div>
      <div style={{ whiteSpace: 'pre-line' }}>{row.activity || ''}</div>
      <div style={{ whiteSpace: 'pre-line', color: row.parallelActivity ? 'inherit' : '#bbb' }}>{row.parallelActivity || '—'}</div>
    </div>
  )
  const byEl = row.by && row.by !== '' && (
    <div title={row.by} style={{ color: row.by === "Auditee's Representative" ? '#888' : '#006363', fontSize: 10, flexShrink: 0, fontStyle: row.by === "Auditee's Representative" ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>{row.by}</div>
  )
  // In a 2-auditor schedule, span the activity across the Lead + Auditor columns so shared rows
  // (Lunch, Opening, Closing, …) align with the parallel rows instead of drifting out of column.
  if (parallelMode) return (
    <div style={{
      display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '0 8px',
      fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)', alignItems: 'baseline',
    }}>
      <div style={{ color: '#888', fontSize: 11 }}>{row.time}</div>
      <div style={{ gridColumn: '2 / 4', display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <span style={{ whiteSpace: 'pre-line' }}>{row.activity}</span>
        {byEl}
      </div>
    </div>
  )
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '80px 1fr auto', gap: '0 8px',
      fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)',
      alignItems: 'baseline',
    }}>
      <div style={{ color: '#888', fontSize: 11 }}>{row.time}</div>
      <div style={{ whiteSpace: 'pre-line' }}>{row.activity}</div>
      {byEl}
    </div>
  )
}

// ─── Collapsible schedule ─────────────────────────────────────────────────────
const PREVIEW_ROWS = 5

export function AuditSchedule({ plan }) {
  const [expanded, setExpanded] = useState(false)
  if (!plan || plan.length === 0) return (
    <div style={{ marginTop: 12, fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: '8px 0' }}>
      Audit schedule not yet available.
    </div>
  )
  const hasParallel = plan.some(r => r.parallel)
  const content = plan.filter(r => !r.isDayHeader && r.time !== 'PARALLEL_HEADER')
  const showToggle = content.length > PREVIEW_ROWS
  let seen = 0
  const preview = plan.filter(r => {
    if (r.isDayHeader || r.time === 'PARALLEL_HEADER') return true
    return ++seen <= PREVIEW_ROWS
  })
  const rows = (expanded || !showToggle) ? plan : preview
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#006363' }}>Audit schedule</div>
        {showToggle && (
          <button onClick={() => setExpanded(e => !e)} style={{
            fontSize: 11, color: '#006363', background: 'none',
            border: '0.5px solid #006363', borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
          }}>
            {expanded ? '▲ Hide' : `▼ Show all ${content.length} rows`}
          </button>
        )}
      </div>
      {!expanded && showToggle && (
        <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontStyle: 'italic' }}>
          Showing first {PREVIEW_ROWS} rows — expand to review full schedule before submitting.
        </div>
      )}
      <div className="sched-screen" style={{ background: '#f7f6f3', borderRadius: 8, padding: '10px 12px' }}>
        {rows.map((row, i) => <ScheduleRow key={i} row={row} i={i} parallelMode={hasParallel} />)}
      </div>
      <div className="sched-print" style={{ display: 'none', background: '#f7f6f3', borderRadius: 8, padding: '10px 12px' }}>
        {plan.map((row, i) => <ScheduleRow key={i} row={row} i={i} parallelMode={hasParallel} />)}
      </div>
      <style>{`@media print{.sched-screen{display:none!important}.sched-print{display:block!important}}`}</style>
    </div>
  )
}

// ─── Audit plan card ──────────────────────────────────────────────────────────
export function AuditPlanCard({ job }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      border: '0.5px solid rgba(0,0,0,0.12)', marginBottom: 16,
    }}>
      <h2 style={{ fontWeight: 500, fontSize: 16, marginBottom: 14 }}>Audit plan</h2>
      <AuditDetailsSection job={job} />
      {(() => {
        if (!job.complexity_classification) return null
        let c = job.complexity_classification
        try { c = typeof c === 'string' ? JSON.parse(c) : c } catch (e) { return null }
        if (!c || !c.complexity) return null
        const overridden = !!c.override_reason
        const row = (label, value) => (
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
            <span style={{ color: '#888', fontWeight: 500 }}>{label}</span>
            <span style={{ fontWeight: label === 'Classification' ? 600 : 400 }}>{value}</span>
          </div>
        )
        return (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#006363', marginBottom: 6 }}>Complexity</div>
            {row('Classification', c.complexity)}
            {row('Basis', overridden ? 'Reviewed and set by Auditing Organisation' : 'System-determined from application details')}
            {row(overridden ? 'Reviewed by' : 'Determined by', `${c.determined_by || 'System'}${c.determined_at ? ' · ' + format(parseISO(c.determined_at), 'd MMM yyyy') : ''}`)}
          </div>
        )
      })()}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#006363', marginBottom: 6 }}>Audit Planning Notes</div>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
          <span style={{ color: '#888', fontWeight: 500 }}>Project Name</span>
          <span style={{ whiteSpace: 'pre-line' }}>{job.project_name || 'Nil'}</span>
        </div>
        {job.audit_approach_note && job.audit_approach_note.trim() && (
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
            <span style={{ color: '#888', fontWeight: 500 }}>Audit Approach Notes</span>
            <span style={{ whiteSpace: 'pre-line' }}>{job.audit_approach_note}</span>
          </div>
        )}
        {job.manday_justification && job.manday_justification.trim() && (
          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.05)' }}>
            <span style={{ color: '#888', fontWeight: 500 }}>Audit Duration Justification</span>
            <span style={{ whiteSpace: 'pre-line' }}>{job.manday_justification}</span>
          </div>
        )}
      </div>
      {job.checklist_url && (
        <div style={{ fontSize: 13, color: '#666', marginTop: 12 }}>
          Audit checklist: <a href={job.checklist_url} target="_blank" rel="noreferrer" style={{ color: '#006363' }}>View →</a>
        </div>
      )}
      <AuditSchedule plan={job.plan_rows_edited || job.plan_rows || []} />
    </div>
  )
}
